import type * as React from "react";
import { Box } from "../../components/ui/box";
import { EmptyState, type EmptyStateSize } from "../../components/ui/empty";
import type { IconName } from "../../components/ui/icon";
import { Text } from "../../components/ui/text";
import { useEntitySelector } from "./entity-selector-context";
import type { Omittable } from "../../lib/props";

/**
 * The selector's empty state. There are TWO of them and they are not
 * interchangeable:
 *
 * - `no-results` — the pool has items and the query matched none. The way out
 *   is to relax the query, so the copy names the search.
 * - `no-items` — the pool is empty before anything is typed. There is no query
 *   to relax; the way out is to go create the thing. Telling someone "no
 *   results found" when they have not searched sends them looking for a filter
 *   that isn't there.
 *
 * A pool that is still fetching is neither: `pool.loading` suppresses both,
 * because an unfetched pool and an empty one have the same item count.
 */

export type EntitySelectorEmptyReason = "no-items" | "no-results";

/** What the empty state is standing in for, for copy that quotes the query. */
export type EntitySelectorEmptyState = {
  reason: EntitySelectorEmptyReason;
  /** The current query. Empty string when `reason` is `no-items`. */
  search: string;
};

/** One state's rendered body. Every part is optional — a bare message is the
 *  common case, and the icon/description/action slots are for a pool whose
 *  emptiness the consumer can actually help resolve. */
export type EntitySelectorEmptyBody = Omittable<{
  message?: React.ReactNode;
  /** Second line, quieter — where the missing items come from. */
  description?: React.ReactNode;
  /** Glyph above the text, drawn in the `EmptyState` circle well. */
  icon?: IconName;
  /** Real Buttons below the text (e.g. one that opens a create flow). */
  action?: React.ReactNode;
}>;

export type EntitySelectorEmptyProps = Omittable<{
  /**
   * Message for whichever state is showing. A function receives the resolved
   * state, for copy that quotes the query. Per-reason props below win over it.
   */
  children?:
    | React.ReactNode
    | ((state: EntitySelectorEmptyState) => React.ReactNode);
  /** Body when a query matched nothing. */
  noResults?: EntitySelectorEmptyBody;
  /** Body when the pool has no items at all. */
  noItems?: EntitySelectorEmptyBody;
  /**
   * The rung a RICH body renders at, by placement: `sm` inside a popover or
   * a small panel (the default — a bare message is unaffected), `default`
   * when the selector fills a dialog frame and the title is its headline.
   */
  size?: Extract<EmptyStateSize, "default" | "sm">;
  /**
   * The glyph's container on a rich body: the quiet `circle` well (default)
   * for a popover that already owns card chrome, or the fanned `icon` chip
   * where the state stands on its own in a frame.
   */
  media?: "icon" | "circle";
}>;

function EntitySelectorEmpty({
  children,
  noResults,
  noItems,
  size = "sm",
  media = "circle",
}: EntitySelectorEmptyProps) {
  const {
    search,
    activePool,
    getPool,
    getFilteredItems,
    getPinnedItems,
    getOmittedCount,
    config,
  } = useEntitySelector();

  const pool = getPool(activePool);
  const query = search.trim();
  const reason: EntitySelectorEmptyReason = query ? "no-results" : "no-items";

  // A virtual pool sized by `total` has rows it has not loaded yet, so an empty
  // loaded prefix is a pending fetch, not an empty pool. Searching bypasses
  // virtual mode (the pool renders dense filtered items), so the guard is
  // limited to the unsearched case.
  const hasUnloadedRows =
    !query && pool?.total != null && pool.total > 0;

  // Rows a selected section took from this pool are on screen, just above it,
  // so a pool emptied that way is not empty: unsearched, everything here is
  // already chosen; searched, the match is the ticked row above. Announcing
  // "nothing here" under either would contradict what the eye can see.
  const listedAbove = getOmittedCount(activePool) > 0;

  const isEmpty =
    pool != null &&
    !pool.loading &&
    !hasUnloadedRows &&
    !listedAbove &&
    getFilteredItems(activePool).length === 0 &&
    getPinnedItems(activePool).length === 0;

  if (!isEmpty) {
    return null;
  }

  const body = (reason === "no-results" ? noResults : noItems) ?? {};

  const fallbackMessage =
    reason === "no-results"
      ? (config.emptyMessage ?? "No results found")
      : (config.noItemsMessage ?? config.emptyMessage ?? "Nothing to select");

  // The function form decides per state, and `null` from it is a decision:
  // nothing stands here for this query. A host whose create row already
  // answers an unmatched query says so this way, instead of the popup saying
  // "no match" over the row that mints one.
  const childMessage =
    typeof children === "function" ? children({ reason, search }) : children;
  if (typeof children === "function" && childMessage === null) return null;

  const message = body.message ?? childMessage ?? fallbackMessage;
  const rich = !!(body.icon || body.description || body.action);

  // A body with a glyph, a second line or an action is a state the consumer
  // can help resolve, so it is the library's one empty state: the glyph in
  // its container, the title, the reason, the remedy. It fills whatever
  // height the host holds open (the picker dialog pins its frame) and centres
  // in it; in a popover it takes its own height.
  if (rich) {
    return (
      <Box
        data-slot="entity-selector-empty"
        direction="column"
        flex={1}
        minHeight={0}
      >
        <EmptyState
          actions={body.action}
          description={body.description}
          media={body.icon ? media : undefined}
          icon={body.icon}
          size={size}
          title={message}
        />
      </Box>
    );
  }

  // A bare message: a centered band with room to breathe. Left-hugging the
  // search well it reads as a stray list row, not a state — centering and
  // the vertical padding are what make an empty popup look intentional
  // rather than broken.
  return (
    <Box
      direction="column"
      align="center"
      justify="center"
      gap="xs"
      paddingX="md"
      paddingY="md"
      data-slot="entity-selector-empty"
    >
      <Text size="xs" variant="muted" align="center">
        {message}
      </Text>
    </Box>
  );
}

EntitySelectorEmpty.displayName = "EntitySelectorEmpty";

export { EntitySelectorEmpty };
